import _, { lowerCase } from "lodash";
import { useState, useEffect } from "react";
import Fuse from "fuse.js";
import { Search } from "lucide-react";
import { Button, cn } from "@shared/ui";
import { fuzzySearchCarriers } from "./Carriers";
import { Row, rowButtonClassName } from "../components/Carrier";
import { inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";

const CREDIT_CARDS = [
  {
    id: "chase-sapphire-reserve",
    name: "Chase Sapphire Reserve",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://creditcards.chase.com/",
    registerUrl:
      "https://creditcards.chase.com/rewards-credit-cards/sapphire/reserve",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
    claimsUrl: "https://www.eclaimsline.com/",
  },
  {
    id: "chase-sapphire-preferred",
    name: "Chase Sapphire Preferred",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://creditcards.chase.com/",
    registerUrl:
      "https://creditcards.chase.com/rewards-credit-cards/sapphire/preferred/test",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
    claimsUrl: "https://www.eclaimsline.com/",
  },
  {
    id: "capital-one-venture-x",
    name: "Capital One Venture X",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://verified.capitalone.com/auth/signin",
    registerUrl: "https://www.capitalone.com/credit-cards/venture-x/",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/capital-one.svg",
    claimsUrl: "https://www.eclaimsline.com/",
  },
  {
    id: "united-explorer",
    name: "United Explorer",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://www2.theexplorercard.com/rewards-cards/explorer-card",
    registerUrl:
      "https://www2.theexplorercard.com/application-processing?SPID=HBTR&CELL=PH6&REFERID=TEC",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
    claimsUrl:
      "https://www.chase.com/personal/credit-cards/united/united-visa-infinite-card/purchase-protection#:~:text=For%20assistance%20with%20all%20United,%2D804%2D673%2D1691.",
  },
  {
    id: "united-quest",
    name: "United Quest",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://www2.theexplorercard.com/rewards-cards/quest-card",
    registerUrl:
      "https://www2.theexplorercard.com/application-processing?SPID=HBJK&CELL=H8X&REFERID=TEC",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
    claimsUrl:
      "https://www.chase.com/personal/credit-cards/united/united-visa-infinite-card/purchase-protection#:~:text=For%20assistance%20with%20all%20United,%2D804%2D673%2D1691.",
  },
  {
    id: "united-club-infinite",
    name: "United Club Infinite",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://www2.theexplorercard.com/rewards-cards/club-card",
    registerUrl:
      "https://www2.theexplorercard.com/application-processing?SPID=HBTR&CELL=PH6&REFERID=TEC",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
    claimsUrl:
      "https://www.chase.com/personal/credit-cards/united/united-visa-infinite-card/purchase-protection#:~:text=For%20assistance%20with%20all%20United,%2D804%2D673%2D1691.",
  },
  {
    id: "united-business",
    name: "United Business",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl:
      "https://www2.theexplorercard.com/business-rewards-cards/business-card",
    registerUrl:
      "https://www2.theexplorercard.com/application-processing?SPID=HBTR&CELL=PH6&REFERID=TEC",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
    claimsUrl:
      "https://www.chase.com/personal/credit-cards/united/united-visa-infinite-card/purchase-protection#:~:text=For%20assistance%20with%20all%20United,%2D804%2D673%2D1691.",
  },
  {
    id: "american-express",
    name: "American Express Premium Rental Car Protection",
    domain: "Credit card add on",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl:
      "https://www.americanexpress.com/en-us/account/login?inav=iNavLnkLog",
    registerUrl:
      "https://feeservices.americanexpress.com/premium/car-rental-insurance-coverage/home.do",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/american-express.svg",
    claimsUrl:
      "https://www.americanexpress.com/en-us/benefits/protections/dashboard/login",
  },
];

const INACTIVE_CREDIT_CARDS = [
  {
    id: "ink-business-preferred",
    name: "Ink Business Preferred",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://www.chase.com/business",
    registerUrl:
      "https://creditcards.chase.com/business-credit-cards/ink/business-preferred",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
  },
  {
    id: "ink-business-cash-credit",
    name: "Ink Business Cash Credit",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://www.chase.com/business",
    registerUrl:
      "https://creditcards.chase.com/business-credit-cards/ink/cash?iCELL=6C1Y",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
  },
  {
    id: "ink-business-unlimited-credit",
    name: "Ink Business Unlimited Credit",
    domain: "Credit card",
    type: "manual",
    status: "active",
    hasImage: true,
    recoveryUrl: "https://www.chase.com/business",
    registerUrl:
      "https://creditcards.chase.com/business-credit-cards/ink/unlimited?iCELL=6C1Y",
    image:
      "https://axle-labs-assets.s3.amazonaws.com/creditCardLogos/chase.svg",
  },
];

const CreditCards = ({
  step,
  setLoginInformation,
  nextStep,
  session,
  posthog,
}) => {
  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog]);
  const creditCards = CREDIT_CARDS;

  const [creditCardName, setCreditCardName] = useState("");
  const [filteredCreditCards, setFilteredCreditCards] = useState(creditCards);

  const filter = (e) => {
    const keyword = e.target.value;

    if (keyword !== "") {
      const lowerCaseKeyword = lowerCase(keyword);
      // Use Fuse for fuzzy search
      const fuse = new Fuse(creditCards, {
        keys: ["name", "domain"],
        threshold: 0.4,
      });
      const uniqueResults = fuzzySearchCarriers(
        fuse,
        lowerCaseKeyword,
        creditCards
      );
      setFilteredCreditCards(uniqueResults);
    } else {
      setFilteredCreditCards(creditCards);
      // If the text field is empty, show all users
    }

    setCreditCardName(keyword);
  };

  const hasResults = filteredCreditCards && filteredCreditCards.length > 0;

  const onCreditCardNotListed = () => {
    posthog.capture("my-credit-card-is-not-listed", {
      carrier: creditCardName,
    });
    setLoginInformation({});
    nextStep("manual-credit-card");
  };

  return (
    <>
      <StepBody>
        <StepHeading title="Choose your credit card" />
        {/* The search sits close to the list it filters. */}
        <div className="flex flex-col gap-3">
          <div className="relative">
            <label htmlFor="credit-card-search" className="sr-only">
              Search credit cards
            </label>
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-ink-secondary"
              strokeWidth={1.75}
            />
            <input
              id="credit-card-search"
              placeholder="Search credit cards"
              className={cn(inputClassName, "pl-10")}
              type="search"
              value={creditCardName}
              onChange={filter}
            />
          </div>
          <p className="sr-only" role="status" aria-live="polite">
            {hasResults
              ? `${filteredCreditCards.length} ${filteredCreditCards.length === 1 ? "credit card" : "credit cards"} found`
              : "No results found"}
          </p>
          <ul className="filteredCarriers divide-y divide-subtle overflow-hidden rounded-lg border border-subtle">
            {hasResults ? (
              _(filteredCreditCards)
                .chain()
                .map((creditCard, index) => (
                  <li key={index}>
                    <button
                      className={rowButtonClassName}
                      onClick={() => {
                        setLoginInformation(creditCard);
                        nextStep("manual-credit-card");
                      }}
                    >
                      <Row
                        backgroundImageUrl={creditCard.image}
                        rowHeader={creditCard.name}
                        rowSubHeader={creditCard.domain}
                      />
                    </button>
                  </li>
                ))
                .value()
            ) : (
              <li className="flex flex-col gap-1 px-4 py-4 text-body-lg ">
                <p className="font-medium text-ink">No results found.</p>
                <p className="text-ink-secondary">
                  Retry, or continue if your credit card isn't listed.
                </p>
              </li>
            )}
          </ul>
        </div>
      </StepBody>
      <StepFooter>
        {hasResults ? (
          <div className="flex justify-center px-2">
            <button
              className="inline-flex min-h-6 items-center rounded-sm text-balance text-center text-body-medium text-ink underline underline-offset-4 hover:no-underline"
              onClick={onCreditCardNotListed}
            >
              My credit card isn’t listed
            </button>
          </div>
        ) : (
          <Button onClick={onCreditCardNotListed}>Continue</Button>
        )}
      </StepFooter>
    </>
  );
};

export default CreditCards;
